import {
    Stack,
    TextField,
    PrimaryButton,
    MessageBar,
    MessageBarType,
    Text,
} from '@fluentui/react';
import React, { useCallback, useContext, useState } from 'react';
import { usePromise } from '../../../../Common/hooks/usePromise';
import { clearCache } from '../../../../AGSS/api/AGSSApi';
import { JsonResult } from '../../../../HubLayout/components/Utilities/JsonResult';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';

interface IClearCacheProps {}

export const ClearCache: React.FC<IClearCacheProps> = (
    props: IClearCacheProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const [envValue, setEnvValue] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const stackTokens = { childrenGap: 16 };

    const handleEnvChange = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string | undefined
    ) => {
        setEnvValue(newValue ?? '');
    };

    const clearCacheCallback = useCallback(async () => {
        if (envValue) {
            const result = await clearCache(envValue);
            setIsLoading(false);
            return result;
        }
    }, [envValue]);

    const [result, error, isLoaded, start, cancel] = usePromise(
        clearCacheCallback,
        false
    );

    const handleClearCacheClick = () => {
        setIsLoading(true);
        start();
    };

    return (
        <Stack tokens={stackTokens}>
            <MessageBar
                messageBarType={MessageBarType.warning}
                styles={{
                    root: {
                        backgroundColor: theme.palette.yellowLight,
                    },
                }}
            >
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                    Warning: This operation will clear cached files for the
                    specified environment.
                </Text>
                <Text variant="small">
                    The operation processes up to ~60,000 files in approximately
                    60 seconds. For large environments, you may need to call
                    this multiple times until all files are cleared.
                </Text>
            </MessageBar>

            <div
                style={{
                    padding: '12px',
                    backgroundColor: theme.palette.white,
                    borderRadius: '4px',
                    border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                }}
            >
                <TextField
                    label="Environment"
                    placeholder="Enter the environment (e.g., usnat.prod, ussec.prod)"
                    value={envValue}
                    onChange={handleEnvChange}
                    description="Specify the cloud environment to clear cache for"
                    borderless
                />
            </div>

            <Stack horizontal tokens={{ childrenGap: 12 }}>
                <PrimaryButton
                    text="Clear Cache"
                    iconProps={{ iconName: 'ClearSelection' }}
                    onClick={handleClearCacheClick}
                    disabled={isLoading || !envValue}
                    styles={{
                        root: {
                            backgroundColor: theme.palette.red,
                            borderColor: theme.palette.red,
                        },
                        rootHovered: {
                            backgroundColor: theme.palette.redDark,
                            borderColor: theme.palette.redDark,
                        },
                    }}
                />
            </Stack>

            {result && result > 0 && (
                <MessageBar messageBarType={MessageBarType.success}>
                    Successfully cleared {result} file(s) from the cache for
                    environment: {envValue}
                </MessageBar>
            )}

            {result !== undefined && result === 0 && (
                <MessageBar messageBarType={MessageBarType.info}>
                    No files found to clear for environment: {envValue}
                </MessageBar>
            )}
        </Stack>
    );
};
